import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, apiError } from "@/lib/api";
import { getStoredRef, clearStoredRef } from "@/lib/refCode";
import { toast } from "sonner";
import ProntoLogo from "@/components/ProntoLogo";
import GoogleHandoff from "@/components/GoogleHandoff";
import { nativeOpenOAuth, isNative as isNativePlatform } from "@/lib/native";
import AppleSignInButton from "@/components/AppleSignInButton";
import { ArrowRight, GoogleLogo, Gift, ShieldCheck, Check, X, Eye, EyeSlash } from "@phosphor-icons/react";

const PASSWORD_SPECIAL_RE = /[^A-Za-z0-9]/;

export default function Register() {
  const { registerJwt, setUser } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [referrer, setReferrer] = useState(null); // { referrer_name, code }

  const pwLongEnough = password.length >= 8;
  const pwHasSpecial = PASSWORD_SPECIAL_RE.test(password);
  const pwValid = pwLongEnough && pwHasSpecial;

  useEffect(() => {
    const code = getStoredRef();
    if (!code) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/referrals/lookup/${code}`);
        if (!cancelled) setReferrer(data);
      } catch {
        // Invalid / expired code — silently drop it
        clearStoredRef();
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // iter v31 r3 — Registrazione in-app abilitata anche su iOS (la versione
  // "Reader Mode" che spediva su browser esterno era una vecchia precauzione
  // anti-steering errata: Apple Guidelines 3.1.1 vieta lo steering SOLO per
  // i pagamenti, NON per la registrazione gratuita).

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!pwLongEnough) { toast.error("La password deve avere almeno 8 caratteri"); return; }
    if (!pwHasSpecial) { toast.error("Aggiungi almeno un carattere speciale (es. ! ? @ # $ %)"); return; }
    setLoading(true);
    try {
      const res = await registerJwt(email, password, name);
      toast.success("Account creato! Controlla l'email per confermare (anche in spam/promozioni).", {
        duration: 7000,
      });
      if (res?.verification_link) {
        sessionStorage.setItem("vista_verify_link", res.verification_link);
      }
      nav("/dashboard");
    } catch (err) {
      toast.error(apiError(err, "Impossibile creare l'account"));
    } finally {
      setLoading(false);
    }
  };

  const onGoogle = () => {
    setGoogleLoading(true);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // iter v29 r195 — Stesso meccanismo di Login.jsx: bridge per native iOS.
    const redirectPath = isNativePlatform ? "/native-oauth-bridge" : "/dashboard";
    const redirectUrl = window.location.origin + redirectPath;
    const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    setTimeout(() => {
      if (isNativePlatform) {
        nativeOpenOAuth(authUrl);
      } else {
        window.location.href = authUrl;
      }
    }, 900);
  };

  return (
    <div className="min-h-screen bg-[#FFFFFF] grid lg:grid-cols-2">
      <GoogleHandoff visible={googleLoading} />
      {/* Brand panel — left */}
      <aside className="hidden lg:flex relative bg-[#7C8B6E] text-[#FFFFFF] flex-col justify-between p-12 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
          backgroundImage:
            "radial-gradient(circle at 25% 25%, #FFFFFF 0%, transparent 35%), radial-gradient(circle at 75% 75%, #7C8B6E 0%, transparent 40%)",
        }} />
        <Link to="/" className="relative z-10 inline-flex" data-testid="brand-link-register-aside">
          <ProntoLogo size={32} tone="light" />
        </Link>
        <div className="relative z-10 space-y-8">
          <p className="font-editorial text-3xl lg:text-4xl leading-[1.1] tracking-tight">
            Iscrizione gratuita.<br/>
            <span className="italic-accent text-[#F1F4ED]">60 crediti in regalo.</span>
          </p>
          <ul className="space-y-3 text-[15px] opacity-90">
            <li className="flex gap-3"><span className="text-[#F1F4ED]">✓</span> 60 crediti gratis — senza carta di credito</li>
            <li className="flex gap-3"><span className="text-[#F1F4ED]">✓</span> Foto da migliorare in 30 secondi con un solo tocco</li>
            <li className="flex gap-3"><span className="text-[#F1F4ED]">✓</span> Pianta catastale arredata 2D in 6 stili</li>
            <li className="flex gap-3"><span className="text-[#F1F4ED]">✓</span> Descrizione + testi pronti per i 4 portali</li>
            <li className="flex gap-3"><span className="text-[#F1F4ED]">✓</span> Live Preview illimitata durante le visite</li>
          </ul>
        </div>
        <div className="relative z-10 text-xs opacity-60 tracking-widest uppercase">
          © {new Date().getFullYear()} Pronto · Pensato in Italia
        </div>
      </aside>

      {/* Form panel — right */}
      <div className="flex flex-col">
        <header className="px-6 lg:px-10 py-4 lg:hidden">
          {/* iter v29 r244+r245 — Solo P-casa verde, centrata. */}
          <div className="flex justify-center">
            <Link to="/" className="inline-flex" data-testid="brand-link-register">
              <img src="/logo/mark-symbol.png?v=8" alt="Pronto" className="h-14 w-14 select-none" draggable="false" />
            </Link>
          </div>
        </header>
        <main className="flex-1 flex items-center justify-center px-6 py-10">
          <div className="w-full max-w-sm">
            <h1 className="font-editorial text-4xl lg:text-5xl leading-tight tracking-tight" data-testid="register-title">
              Crea il tuo account.
            </h1>
            <p className="mt-2 text-[#6B6A65]">60 crediti gratis. Nessuna carta.</p>

            {referrer && (
              <div
                className="mt-6 flex items-start gap-3 px-4 py-3 bg-[#F1F4ED] border-l-2 border-[#7C8B6E]"
                data-testid="referral-banner"
              >
                <Gift size={20} weight="duotone" className="text-[#7C8B6E] mt-0.5 shrink-0" />
                <div className="text-sm leading-relaxed">
                  <div className="font-medium text-[#1C1C1A]">
                    Ti ha invitato <span className="text-[#7C8B6E]">{referrer.referrer_name}</span>.
                  </div>
                  <div className="text-[#6B6A65] mt-0.5">
                    Quando passerai a Pro, ricambieremo il favore: lui riceve 30 crediti gratuiti. Tu ricevi subito 30 crediti di benvenuto, e sei libero di restare gratuita quanto vuoi.
                  </div>
                </div>
              </div>
            )}

            <button
              onClick={onGoogle}
              className="mt-8 w-full flex items-center justify-center gap-3 border border-[#E5E7E0] bg-white px-4 py-3 rounded-sm hover:border-[#7C8B6E] transition-colors"
              data-testid="google-register-btn"
            >
              <GoogleLogo size={20} weight="bold" className="text-[#7C8B6E]" />
              <span className="font-medium">Registrati con Google</span>
            </button>

            {/* iter v29 r168 — Apple guideline 4.8 · Sign in with Apple
                obbligatorio se c'è Google. Self-hides su web/Android. */}
            <div className="mt-3">
              <AppleSignInButton
                onSetUser={setUser}
                onAuthenticated={() => nav("/dashboard")}
              />
            </div>

            <div className="my-6 flex items-center gap-3 text-xs text-[#6B6A65] uppercase tracking-[0.2em]">
              <span className="flex-1 h-px bg-[#E5E7E0]" />
              oppure
              <span className="flex-1 h-px bg-[#E5E7E0]" />
            </div>

            <form onSubmit={onSubmit} className="space-y-3">
              <input type="text" required value={name} onChange={(e) => setName(e.target.value)} data-vista data-testid="register-name-input" placeholder="Nome" />
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-vista data-testid="register-email-input" placeholder="Email" />
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"} required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-vista data-testid="register-password-input"
                  placeholder="Password"
                  className="pr-10 w-full"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B6A65] hover:text-[#1C1C1A] transition-colors"
                  data-testid="register-toggle-password"
                  aria-label={showPassword ? "Nascondi password" : "Mostra password"}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeSlash size={18} weight="regular" /> : <Eye size={18} weight="regular" />}
                </button>
              </div>
              {password.length > 0 && (
                <div className="flex items-center gap-4 text-[11.5px] text-[#6B6A65] pl-1" data-testid="password-strength">
                  <span className={`flex items-center gap-1 ${pwLongEnough ? "text-[#7C8B6E]" : ""}`}>
                    {pwLongEnough ? <Check size={13} weight="bold" /> : <X size={13} weight="bold" className="text-[#C9A875]" />}
                    Almeno 8 caratteri
                  </span>
                  <span className={`flex items-center gap-1 ${pwHasSpecial ? "text-[#7C8B6E]" : ""}`}>
                    {pwHasSpecial ? <Check size={13} weight="bold" /> : <X size={13} weight="bold" className="text-[#C9A875]" />}
                    1 carattere speciale
                  </span>
                </div>
              )}
              <button type="submit" disabled={loading || !pwValid} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="register-submit-btn">
                {loading ? "Creazione..." : <>Crea account <ArrowRight size={16} weight="bold" /></>}
              </button>
            </form>

            <div className="mt-6 flex items-start gap-2.5 text-[11.5px] text-[#6B6A65] leading-relaxed" data-testid="register-trust-strip">
              <ShieldCheck size={14} weight="duotone" className="text-[#7C8B6E] shrink-0 mt-0.5" />
              <span>
                I tuoi dati restano in <strong className="text-[#1C1C1A]">Europa</strong>. Connessione cifrata, password protetta con bcrypt. Niente profilazione, niente rivendita. <Link to="/terms" className="underline hover:text-[#7C8B6E]">Privacy &amp; sicurezza →</Link>
              </span>
            </div>

            <p className="mt-8 text-sm text-[#6B6A65] text-center">
              Hai già un account?{" "}
              <Link to="/login" className="text-[#7C8B6E] font-medium underline" data-testid="to-login-link">
                Accedi
              </Link>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
